import cn from 'classnames';
import Link from 'next/link';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  CustomerSubscriptionSource,
  CustomerSubscriptionStatus,
} from '@lambda/apis/src/customer/enum';
import { CtaButton } from '@/components/Cta/Cta';
import useLoading from '@/lib/use-loading';
import { logger } from '@/lib/logger';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import i18n from '@/settings/i18n';

const t = i18n.init('shared');

export default function SubscribeNewsletter(props: { className?: string }) {
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  const { updateEmailSubscription } = useCustomerContext();
  const consentMessage = t`Unlock exclusive discounts, new arrivals, and daily deals.`;

  if (!recaptchaSiteKey) {
    logger.warn('Recaptcha site key is missing');
  }

  const [recaptchaMounted, setRecaptchaMounted] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const form = useForm({
    resolver: yupResolver(
      yup
        .object()
        .shape({
          email: yup
            .string()
            .trim()
            .email(t`Oops, this is not a valid email. Let's try again`)
            .required(t`Hey, you forgot to set your email.`),
        })
        .required(),
    ),
  });
  const [success, setSuccess] = useState(false);
  const [loading, subscribe] = useLoading(async (email: string) => {
    try {
      const token = await recaptchaRef.current?.executeAsync();

      if (!token) {
        throw new Error('Recaptcha token is missing');
      }

      await updateEmailSubscription({
        email,
        status: CustomerSubscriptionStatus.OPTED_IN,
        source: CustomerSubscriptionSource.FOOTER,
        consentMessage,
      });

      setSuccess(true);

      form.reset();
    } catch (error) {
      setSuccess(false);
    }
  });

  const error = form.formState.errors.email?.message;

  return (
    <form
      onSubmit={form.handleSubmit(({ email }) => subscribe(email))}
      onFocus={() => setRecaptchaMounted(true)}
    >
      <div
        className={cn(
          'grid w-full text-center text-xs sm:max-w-[320px]',
          props.className,
        )}
      >
        <div>{consentMessage}</div>
        <input
          placeholder={t`Enter your email`}
          className="mt-1 rounded rounded-b-none p-3 text-sm text-gray-700 outline-none"
          aria-label="Enter email to subscribe"
          type="email"
          {...form.register('email')}
        />
        <CtaButton
          type="submit"
          disabled={success}
          variant="teal"
          loading={loading}
          className="!my-0 !rounded-t-none disabled:opacity-50"
        >
          {success ? t`All good! You are subscribed` : t`Subscribe`}
        </CtaButton>
        {error && <div className="text-red">{error}</div>}
        <div className="mt-1">
          {t`By subscribing, you accept our`}{' '}
          <Link href="/policies/privacy-policy" className="underline">
            {t`Privacy Policy`}
          </Link>
        </div>
        {recaptchaSiteKey && (
          <>
            <div className="mt-2">
              {t`This site is protected by reCAPTCHA and the Google`}
              <a href="https://policies.google.com/privacy">
                {' '}
                {t`Privacy Policy`}
              </a>{' '}
              {t`and`}
              <a href="https://policies.google.com/terms">
                {' '}
                {t`Terms of Service`}
              </a>{' '}
              {t`apply.`}
            </div>
            {recaptchaMounted && (
              <ReCAPTCHA
                size="invisible"
                ref={recaptchaRef}
                sitekey={recaptchaSiteKey}
              />
            )}
          </>
        )}
      </div>
    </form>
  );
}
